import React from 'react';
import { Project } from '../../types';

/**
 * Initials from a display name: "Anders Melin" -> "AM".
 *
 * Same convention the calendar uses (getEvaluatorInitials in
 * functions/src/calendar.ts) and the same one the team already writes by hand
 * in event titles ("AM - Anna Stålnacke, ..."), so the initials on a card read
 * the same as the initials in Google Calendar.
 */
export function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * Which people currently own this project.
 *
 * Ownership moves with the lifecycle: a home belongs to the valuator until the
 * valuation is done, then it belongs to the pickup team. Showing both at all
 * times would put up to four sets of initials on a card (38 of 60 live projects
 * carry an evaluator AND a 2-3 person team), competing with the date and status
 * for the card's right-hand space. Showing whoever owns it *now* answers the
 * only question the card needs to answer: whose job is this?
 */
export function getAssignees(project: Project): string[] {
  // 'värderad' is labelled "Klar för hämtning" — the valuation is done and the
  // home is waiting on the pickup team, so it belongs to them from that point
  // on, not from the moment a date is booked. Ownership hands over when the
  // work does.
  const isPickupStage =
    project.status === 'värderad' ||
    project.status === 'planerad' ||
    project.status === 'avslutad';

  if (isPickupStage && project.pickupTeam?.length) {
    return splitNames(project.pickupTeam);
  }
  // No team assigned yet (or an earlier stage): show the valuator, who still
  // owns it. Several finished projects have no recorded team, so this fallback
  // keeps their cards from going blank.
  if (project.evaluator) return splitNames([project.evaluator]);
  return splitNames(project.pickupTeam ?? []);
}

/**
 * Expand comma-joined names into individual people.
 *
 * `evaluator` is a single string, but a shared visit stores both names in it
 * ("Peter Webb, Amos Fisk" — the calendar import writes this for titles like
 * "PW AF - ..."). Treating that as one person produced two bugs: the initials
 * came from the first two words only (so "PW", hiding Amos Fisk entirely), and
 * the tint hashed the whole string, so "Peter Webb" and "Peter Webb, Amos Fisk"
 * rendered the same "PW" in different colours.
 */
function splitNames(values: string[]): string[] {
  const out: string[] = [];
  for (const value of values) {
    for (const name of value.split(',')) {
      const trimmed = name.trim();
      if (trimmed && !out.includes(trimmed)) out.push(trimmed);
    }
  }
  return out;
}

/**
 * Deterministic tint per person, so the same face keeps the same colour on
 * every card. Known people are pinned so their colour never shifts if the
 * hash function or the palette changes; anyone else falls back to the hash.
 *
 * Pinning also avoids collisions inside one card: with 5 tints and a 3-person
 * team, two members landing on the same colour was likely (Peter Webb and
 * Carianne Öberg both hashed to accent).
 */
const TINTS = [
  'bg-accent-100 text-accent-700 dark:bg-accent-900/40 dark:text-accent-300',
  'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
  'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
];

/** Stable colour index for the people who actually appear in this app. */
const PINNED_TINT: Record<string, number> = {
  'Anders Melin': 1,
  'Peter Webb': 0,
  'Niklas Sköld': 2,
  'Carianne Öberg': 3,
  'Patrik Collén': 4,
  'Amos Fisk': 5,
  'Jan Öberg': 6,
  'Ilona Sweder': 2,
  'Daniel Westin': 3,
};

function tintFor(name: string): string {
  const pinned = PINNED_TINT[name];
  if (pinned !== undefined) return TINTS[pinned];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return TINTS[Math.abs(hash) % TINTS.length];
}

interface AssigneeAvatarsProps {
  names: string[];
  /** Cap before collapsing into "+N". Keeps a 3-person team from crowding the row. */
  max?: number;
  className?: string;
}

export function AssigneeAvatars({ names, max = 3, className = '' }: AssigneeAvatarsProps) {
  if (!names.length) return null;

  const shown = names.slice(0, max);
  const overflow = names.length - shown.length;

  return (
    // The full names go on the group, not on each circle: initials are
    // ambiguous ("AM" could be two people), and a screen reader should read
    // "Anders Melin", not "A M".
    // gap, not negative space: overlapping the circles clipped the trailing
    // letter of the one underneath, so "PW" read as "PV". Wide glyphs (W, M)
    // reach the edge of a 24px circle, and there is nothing to spare.
    <div
      className={`flex items-center gap-1 ${className}`}
      role="img"
      aria-label={`Tilldelad: ${names.join(', ')}`}
    >
      {shown.map(name => (
        <span
          key={name}
          title={name}
          aria-hidden="true"
          className={`inline-flex items-center justify-center min-w-[26px] h-[22px] px-1 rounded-full text-[11px] leading-none font-semibold tracking-[0.01em] ${tintFor(name)}`}
        >
          {getInitials(name)}
        </span>
      ))}
      {overflow > 0 && (
        <span
          aria-hidden="true"
          className="inline-flex items-center justify-center min-w-[26px] h-[22px] px-1 rounded-full text-[11px] leading-none font-semibold tabular-nums bg-[var(--bg-surface-alt)] text-[var(--text-secondary)]"
        >
          +{overflow}
        </span>
      )}
    </div>
  );
}

export default AssigneeAvatars;
